import type { FC, HTMLAttributes } from 'react';
import { memo } from 'react';
import { twMerge } from 'tailwind-merge';

import { useWebsite } from '../../../../hooks/useWebsite';
import type { Category, Topic } from '../../../../types/api';
import { CategoryBadge } from '../../../Category/CategoryBadge';
import { FacetBadges } from '../../../Category/FacetBadges';

type TopicItemCategoriesProps = HTMLAttributes<HTMLDivElement> & {
  topic: Topic;
  isResponsive?: boolean;
};

const TopicItemTags: FC<TopicItemCategoriesProps> = memo(function TopicItemTags(props) {
  const { topic, className, isResponsive = true, ...otherProps } = props;
  const { categories } = topic;
  const { graphdebate } = useWebsite();

  // Facettes theme/entity (B2_05) — mode média uniquement (D23 : cartes forum exclues).
  // Un article média peut porter des facettes sans catégorie visible → le bloc se rend
  // aussi dans ce cas (l'ancien early-return `categories?.length` ne suffit plus).
  const isMediaMode = graphdebate?.mode === 'media';
  const hasFacets =
    isMediaMode && Boolean(topic.themes?.some(f => f?.id) || topic.entities?.some(f => f?.id));

  return categories?.length || hasFacets ? (
    <div
      className={twMerge(
        'TopicItemTags mt-3 flex flex-wrap gap-2',
        isResponsive && 'md:gap-4 md:mt-4',
        className,
      )}
      {...otherProps}
    >
      {((topic.categories ?? []) as Category[])
        .reduce<Category[]>((acc, c) => (acc.some(cat => cat.id === c.id) ? acc : [...acc, c]), [])
        .map(cat => (
          <CategoryBadge
            key={cat.id}
            slug={cat.slug}
            title={cat.title}
            variant="contained"
            className={twMerge(
              'w-fit text-[11px] text-gray-soft bg-gray-60/100 hover:text-primary-dark hover:bg-red-60/100',
              isResponsive && 'text-[12px] md:text-[15px]',
            )}
          />
        ))}
      {hasFacets && (
        <FacetBadges
          themes={topic.themes}
          entities={topic.entities}
          className="contents"
          badgeClassName={twMerge(
            'w-fit text-[11px] text-gray-soft bg-gray-60/100 hover:text-primary-dark hover:bg-red-60/100',
            isResponsive && 'text-[12px] md:text-[15px]',
          )}
        />
      )}
    </div>
  ) : null;
});

export default TopicItemTags;
